import React, { useState, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Stack,
  useColorModeValue,
  useBreakpointValue,
  Heading,
  Image,
} from "@chakra-ui/react";
import { RegisterForm } from "../../../login/components/RegisterForm";
import { useDispatch, useSelector } from "react-redux";
import { useUserProfile } from "hooks";
import { track } from "api/analytics";
import { REGISTRATION_FAILED, REGISTRATION_POST_SETUP, REGISTRATION_ACCOUNT_SUBMITTED } from "api/analytics/events";
import type { RootState } from "state/rootReducer";
import { shallowEqual } from "react-redux";
import { useCopyValue } from "hooks/useCopies";
import { useConversationContext } from "../../../thread/ConversationContext";
import targetImage from "screens/common/static/misc/target_image.png";
import { updateTypedUserPreference } from "state/userPreference/operations";

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (fields: { firstName: string; lastName: string; username: string; investmentInterest: string; password: string }) => void;
}

interface RegistrationState {
  isCheckingAccount: boolean;
  isRegistered: boolean;
  loadingText: string;
  hasReceivedResponse: boolean;
}

export const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [state, setState] = useState<RegistrationState>({
    isCheckingAccount: false,
    isRegistered: false,
    loadingText: "Preparing your account...",
    hasReceivedResponse: false,
  });
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const bgColor = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.700", "gray.200");
  const registrationFormTitle = useCopyValue("copy_registration_form_title");
  const { setConversationId, setShouldClearInputValue } = useConversationContext();
  const dispatch = useDispatch();

  const { email, fullName } = useUserProfile();
  const { lastError, isLoggedIn, isLoading, user } = useSelector((state: RootState) => state.session, shallowEqual);

  // Check if user is a real registered user (not anonymous)
  const isRealUser = isLoggedIn && user && !user.isAnonymous;

  // Handle registration state changes
  React.useEffect(() => {
    if (!state.isCheckingAccount) return;

    // If user becomes a real registered user (not anonymous), registration was successful
    if (isRealUser) {
      track(REGISTRATION_POST_SETUP, { userName: fullName, email });
      setState((prev) => ({
        ...prev,
        isCheckingAccount: false,
        isRegistered: true,
      }));

      // Reset conversation state to clear any anonymous user conversation context
      setConversationId("");
      setShouldClearInputValue(true);

      // Close modal and stay on current page - user is now logged in
      onClose();
      return;
    }

    // If there's an error and we're not loading anymore
    if (lastError && !isLoading) {
      track(REGISTRATION_FAILED, { userName: fullName, email, error: lastError });
      setState((prev) => ({
        ...prev,
        isCheckingAccount: false,
        isRegistered: false,
      }));
    }
  }, [lastError, isRealUser, isLoading, state.isCheckingAccount, fullName, email, onClose, setConversationId, setShouldClearInputValue]);

  // Update local loading state based on Redux loading state
  React.useEffect(() => {
    if (state.isCheckingAccount && !isLoading && !isRealUser && !lastError) {
      // If we were checking account but Redux is no longer loading and no success/error,
      // reset the local state
      setState((prev) => ({
        ...prev,
        isCheckingAccount: false,
      }));
    }
  }, [state.isCheckingAccount, isLoading, isRealUser, lastError]);

  // Reset hasReceivedResponse when starting a new registration attempt
  const handleRegistrationSubmit = useCallback(
    (fields: { firstName: string; lastName: string; username: string; investmentInterest: string; password: string }) => {
      track(REGISTRATION_ACCOUNT_SUBMITTED, { userName: fullName, email });
      setState((prev) => ({
        ...prev,
        isCheckingAccount: true,
        hasReceivedResponse: false,
      }));
      onSubmit(fields);
      dispatch(updateTypedUserPreference({ preferenceKey: "ui_onboarding_completed", value: true }));
    },
    [dispatch, email, fullName, onSubmit]
  );

  const handleClose = useCallback(() => {
    // Reset state when closing
    setState({
      isCheckingAccount: false,
      isRegistered: false,
      loadingText: "Preparing your account...",
      hasReceivedResponse: false,
    });
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent className="ch-registration-modal" bg={bgColor} borderRadius={{ base: 0, lg: "xl" }}>
        <ModalBody p={"2rem"} borderRadius={{ base: 0, lg: "xl" }}>
          <Box flex="5" position={"relative"}>
            <ModalCloseButton zIndex={3} />
            <Box position={"absolute"} top="-3.9rem" left="2.2rem" transform={"translateX(-50%)"} width="13rem">
              <Image src={targetImage} width="100%" opacity={0.6} />
            </Box>
            <Box width={isMobile ? "100%" : "420px"} mx="auto">
              <Stack spacing={6}>
                <Heading zIndex={2} as="h3" size="lg" textAlign="left" color={headingColor}>
                  {registrationFormTitle}
                </Heading>
                <Box>
                  {!state.isRegistered && !isRealUser && (
                    <RegisterForm
                      onSubmit={handleRegistrationSubmit}
                      isCheckingAccount={state.isCheckingAccount || isLoading}
                      usernameError={lastError}
                    />
                  )}
                </Box>
              </Stack>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
